// Ändere die API-Basis-URL basierend auf der Umgebung
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? `${window.location.protocol}//${window.location.host}/api`  // In Produktion: Verwendet nginx proxy
  // In Entwicklung: Backend direkt auf Port 8000. `hostname` statt "localhost",
  // damit die App auch von anderen Geräten im Netz funktioniert — dort zeigt
  // "localhost" sonst auf das Gerät selbst. Die Origin muss dann in den
  // CORS-Origins des Backends stehen (IPAD_DOMAIN in der .env).
  : `${window.location.protocol}//${window.location.hostname}:8000/api`;

/** Fehler aus ApiClient.fetch — ein Error mit angehefteten Request-/Response-Infos. */
export interface ApiError extends Error {
  status?: number;
  isUnauthorized?: boolean;
  isTimeout?: boolean;
  timeoutMs?: number;
  /** Geparster Response-Body des Fehlers (Server-JSON, Form ist endpunktabhängig). */
  originalErrorData?: any;
  url?: string;
  method?: string;
}

/** RequestInit mit typisierten Headern + eigenem Timeout-Feld. */
export type ApiRequestOptions = Omit<RequestInit, 'headers'> & {
  headers?: Record<string, string>;
  timeoutMs?: number;
};

// Aktive Anfragen verfolgen
const activeRequests = new Map<string, AbortController>();

// Token-Refresh State (Modul-Scope, damit parallele Requests queued werden)
let _isRefreshing = false;
let _refreshQueue: Array<(success: boolean) => void> = [];

async function attemptTokenRefresh(): Promise<boolean> {
    if (_isRefreshing) {
        // Warte auf laufenden Refresh statt einen zweiten zu starten
        return new Promise((resolve) => { _refreshQueue.push(resolve); });
    }
    _isRefreshing = true;
    try {
        const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
            method: 'POST',
            credentials: 'include',
        });
        const success = response.ok;
        _refreshQueue.forEach(resolve => resolve(success));
        return success;
    } catch {
        _refreshQueue.forEach(resolve => resolve(false));
        return false;
    } finally {
        _refreshQueue = [];
        _isRefreshing = false;
    }
}

class ApiClient {
    // Abbrechen aller aktiven Anfragen mit dem gleichen Endpunkt
    static abortActiveRequests(endpoint: string): void {
        const key = endpoint.toString();
        const controller = activeRequests.get(key);
        if (controller) {
            console.log(`Aborting previous request to ${key}`);
            controller.abort();
            activeRequests.delete(key);
        }
    }

    static async fetch(url: string, options: ApiRequestOptions = {}, _retry = false): Promise<Response> {
        const timeoutMsRaw = Number(options.timeoutMs);
        const timeoutMs = Number.isFinite(timeoutMsRaw) && timeoutMsRaw > 0 ? timeoutMsRaw : 30000;
        const requestOptions = { ...options };
        delete requestOptions.timeoutMs;

        // Erstelle einen AbortController für diese Anfrage, wenn keiner übergeben wurde
        let controller: AbortController | undefined;
        let externalSignal = false;
        let didTimeout = false;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;

        if (requestOptions.signal) {
            // Verwende das übergebene Signal
            externalSignal = true;
        } else {
            // Erstelle einen neuen Controller
            controller = new AbortController();
            requestOptions.signal = controller.signal;
        }

        // Füge API_BASE_URL zu allen Requests hinzu
        // Entferne führenden Slash von der URL, wenn vorhanden
        const cleanUrl = url.startsWith('/') ? url.slice(1) : url;
        const fullUrl = `${API_BASE_URL}/${cleanUrl}`;
        // I3: Dedupe/Abort-Key inkl. HTTP-Methode, damit ein PUT nicht einen laufenden
        // GET auf denselben Pfad abbricht (AbortError beim falschen Caller) und umgekehrt.
        const requestKey = `${(requestOptions.method || 'GET').toUpperCase()}:${fullUrl}`;

        // Breche vorherige Anfragen nur ab, wenn kein externes Signal übergeben wurde
        if (!externalSignal && controller) {
            this.abortActiveRequests(requestKey);
            // Speichere den Controller für diese Anfrage
            activeRequests.set(requestKey, controller);
        }

        // Stelle sicher, dass credentials immer included sind
        const finalOptions: RequestInit = {
            ...requestOptions,
            credentials: 'include',
        };

        // Füge Header nur hinzu, wenn es sich nicht um FormData handelt
        if (!(requestOptions.body instanceof FormData)) {
            finalOptions.headers = {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                ...requestOptions.headers,
            };
        }

        try {
            // Setze einen Timeout für die Anfrage
            timeoutId = setTimeout(() => {
                if (!externalSignal && activeRequests.has(requestKey)) {
                    console.warn(`Request timeout for ${fullUrl}`);
                    didTimeout = true;
                    controller?.abort();
                    activeRequests.delete(requestKey);
                }
            }, timeoutMs);

            const response = await fetch(fullUrl, finalOptions);

            // Bereinige nach erfolgreicher Anfrage
            clearTimeout(timeoutId);
            activeRequests.delete(requestKey);

            // Bei !ok: Fehlerbehandlung
            if (!response.ok) {
                // Body nur bei Fehlern lesen
                let responseBody = '';
                try {
                    const clonedResponse = response.clone();
                    responseBody = await clonedResponse.text();
                } catch (e) {
                    // Ignore body read errors
                }

                // 401: Token-Refresh versuchen (einmalig, nicht für Auth-Endpoints selbst)
                const isAuthEndpoint = fullUrl.includes('/auth/refresh') ||
                    fullUrl.includes('/auth/login') ||
                    fullUrl.includes('/auth/logout');

                if (response.status === 401 && !_retry && !isAuthEndpoint) {
                    const refreshed = await attemptTokenRefresh();
                    if (refreshed) {
                        return ApiClient.fetch(url, options, true);
                    }
                }

                // 401-Allowlist: Diese Endpoints feuern kein 'unauthorized' Event
                const is401Allowlisted = fullUrl.includes('/login') ||
                    fullUrl.includes('/register') ||
                    fullUrl.includes('/reset-password') ||
                    fullUrl.includes('/me');

                // 401-Handling: Event + Throw
                if (response.status === 401 && !is401Allowlisted) {
                    localStorage.setItem('sessionExpired', 'true');
                    localStorage.setItem('lastPath', window.location.pathname);
                    window.dispatchEvent(new Event('unauthorized'));

                    const error = new Error('Session abgelaufen') as ApiError;
                    error.status = 401;
                    error.isUnauthorized = true;
                    throw error;
                }

                // Fehler-Extraktion aus Response-Body
                let errorMessage = `Fehler ${response.status}`;
                let errorData: any = null;

                try {
                    if (responseBody) {
                        errorData = JSON.parse(responseBody);
                        if (errorData.detail) {
                            if (typeof errorData.detail === 'object') {
                                errorMessage = JSON.stringify(errorData.detail);
                            } else {
                                errorMessage = errorData.detail;
                            }
                        }
                    }
                } catch (e) {
                    console.warn('Could not parse error response:', e);
                }

                // Create a custom error that preserves the original error data
                const error = new Error(errorMessage) as ApiError;
                error.status = response.status;
                error.originalErrorData = errorData;
                error.url = fullUrl;
                error.method = finalOptions.method;
                throw error;
            }

            return response;

        } catch (caught) {
            const error = caught as ApiError;
            if (timeoutId !== null) clearTimeout(timeoutId);
            // Bereinige bei Fehler
            activeRequests.delete(requestKey);

            if (didTimeout && error.name === 'AbortError') {
                const timeoutError = new Error(`Request timeout after ${timeoutMs}ms`) as ApiError;
                timeoutError.name = 'TimeoutError';
                timeoutError.isTimeout = true;
                timeoutError.timeoutMs = timeoutMs;
                timeoutError.url = fullUrl;
                timeoutError.method = finalOptions.method;
                throw timeoutError;
            }

            // AbortError: Original weiterwerfen (nicht maskieren!)
            if (error.name === 'AbortError') {
                console.log('Request aborted:', fullUrl);
                throw error;
            }

            // Alle anderen Fehler (inkl. unserer eigenen mit status/originalErrorData)
            console.error('API request failed:', error);
            throw error;
        }
    }

    // Hilfsmethoden für häufige Operationen mit Debouncing
    static get(url: string, options: ApiRequestOptions = {}): Promise<Response> {
        return this.fetch(url, { method: 'GET', ...options });
    }

    static post(url: string, data: unknown, options: ApiRequestOptions = {}): Promise<Response> {
        // FormData nicht JSON-serialisieren
        const body = data instanceof FormData ? data : JSON.stringify(data);
        return this.fetch(url, {
            method: 'POST',
            body,
            ...options
        });
    }

    static put(url: string, data: unknown, options: ApiRequestOptions = {}): Promise<Response> {
        // FormData nicht JSON-serialisieren
        const body = data instanceof FormData ? data : JSON.stringify(data);
        return this.fetch(url, {
            method: 'PUT',
            body,
            ...options
        });
    }

    static patch(url: string, data: unknown, options: ApiRequestOptions = {}): Promise<Response> {
        // FormData nicht JSON-serialisieren
        const body = data instanceof FormData ? data : JSON.stringify(data);
        return this.fetch(url, {
            method: 'PATCH',
            body,
            ...options
        });
    }

    static delete(url: string, options: ApiRequestOptions = {}): Promise<Response> {
        return this.fetch(url, { method: 'DELETE', ...options });
    }

    static upsert(url: string, data: unknown, options: ApiRequestOptions = {}): Promise<Response> {
        // FormData nicht JSON-serialisieren
        const body = data instanceof FormData ? data : JSON.stringify(data);
        return this.fetch(url, {
            method: 'POST',
            body,
            ...options
        });
    }

    // postFormData ist jetzt ein Alias für post() mit FormData
    // (Die Fehlerbehandlung passiert automatisch in fetch())
    static postFormData(url: string, formData: FormData, options: ApiRequestOptions = {}): Promise<Response> {
        return this.post(url, formData, options);
    }

    // Methode zum Abbrechen aller aktiven Anfragen
    static abortAll(): void {
        console.log(`Aborting all ${activeRequests.size} active requests`);
        for (const [url, controller] of activeRequests.entries()) {
            console.log(`Aborting request to ${url}`);
            controller.abort();
        }
        activeRequests.clear();
    }
}

export default ApiClient;
